import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { isValid} from 'date-fns';
import * as _ from 'lodash-es';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { HolidayService } from '../services/holiday.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { IHoliday } from '../shared/models/holiday';
import { ToastService } from '../shared/toast/toast.service';
import { DeleteActionComponent } from './delete-action.component';
import { IEmployer, IEmployerViewModel } from '../shared/models/employer';
import { EmployerService } from '../services/employer.service';
import { GridCheckboxComponent } from './grid-checkbox.component';
import { DialogService } from 'primeng/dynamicdialog';
import { EntityFormComponent } from './entity-form.component';

@Component({
  selector: 'pps-manage-employer',
  templateUrl: './manage-employer.component.html',
  styles: [
  ],
})
export class ManageEmployerComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Company", icon: "pi-fw fas fa-city"},
  ];

  oldEmployers: IEmployerViewModel[] = [];
  employers: IEmployerViewModel[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => param.data.id,
    defaultColDef:{
      enableCellChangeFlash: true,
      resizable: true
    }
  };

  private gridApi;

  constructor(private _employerService: EmployerService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private _dialog: DialogService,
              private _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver,) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }

  addEmployer(){
    let employer: IEmployer = {
      id: 0,
      employerName: "",
      active: true
    };

    let dialogOption: any = {
      backdrop: 'static',
      size: "550"
    };

    let dialog = this._dialog.open(EntityFormComponent, {
      header: `Add Company`,
      styleClass: "size-sm",
      data: {
        entity: employer,
        categoryName: "Company"
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(entity => {
      if (entity)
      {
        entity.employerName = entity.name;
        this.addEmployerHelper(entity);
      }},
      reason => {
        console.log(reason);
      }
    );
  }

  private addEmployerHelper(employer: IEmployer){
    this._spinner.show(this.spinnerName);

    this.subs.sink = this._employerService.add(employer)
      .subscribe(res => {
        if (res.succeed){
          this.employers.unshift(res.result);
          this.oldEmployers = _.cloneDeep(this.employers);
          this.gridApi.setRowData(this.employers);
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.employers.length = 0;
    this.subs.sink = this._employerService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.employers = apiResponse.result;
          this.oldEmployers = _.cloneDeep(this.employers);
          this._toast.succeeded(`${this.employers.length} records loaded`);
        }else{
          this._toast.failed(`Failed to load employer data: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  updateData(employer: IEmployerViewModel){
    console.log(employer);
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._employerService.update(employer)
      .subscribe(res => {
        if (res.succeed){
          this._toast.succeeded(`Company updated`);
          employer = res.result;
          
          let empIndex = this.employers.findIndex(e=>e.id === employer.id);
          if (empIndex !== -1){
            this.employers[empIndex] = employer;
          }

          this.gridApi.setRowData(this.employers);
          this.oldEmployers = _.cloneDeep(this.employers);
        }else{
          this._toast.failed(`Company update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  private getColumnDefs(){
    let cols = [
      {
        colId: "employerName",
        headerName: 'Company Name',
        field:'employerName',
        filter: true,
        floatingFilter: true,
        width: 500,
        editable: true,
        sortable: true,
        onCellValueChanged: this.dataChanged,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "active",
        headerName: "Active",
        field: "active",
        sortable: true,
        width: 200,
        cellRenderer: GridCheckboxComponent,
        cellRendererParams: {
          checkboxValueColId: "active"
        },
        cellStyle: {textAlign:"center"}
      },
      {
        colId: "addBy",
        headerName: 'Added By',
        field:'addBy',
        filter: true,
        floatingFilter: true,
        width: 150,
        editable: false,
        sortable: true,
        onCellValueChanged: this.dataChanged,
        headerClass: "ag-ext-header-align-left"
      },
      {
        headerName: 'Date Added',
        field:'addDate',
        filter: true,
        floatingFilter: true,
        width: 150,
        editable: false,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        cellRenderer: this.getDateValue
      },
    ];

    return cols;
  }

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateTimeString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }

  private dataChanged(params){
    let _this = params.context.componentParent;
    let employer: IEmployer = params.data;

    _this.updateData(employer);
  }
}
